import type { OnInit } from '@angular/core';
import { Component, HostBinding, Inject, Input } from '@angular/core';
import type { BasicContentValues } from '@legacy/common-ui-common-renderings';
import { getBasicContentValuesFromRendering } from '@legacy/common-ui-common-renderings';
import type { ButtonTheme, ContentTheme } from '@legacy/common-ui-partials';
import type { BackgroundSettings } from '@legacy/common-ui-shared';
import { getBackgroundSettingsFromRendering } from '@legacy/common-ui-shared';
import type {
  ColumnConfig,
  DynamicRowSettings,
} from '@legacy/common-ui-shared-interfaces';
import type { EnvConfigBrand } from '@core/config-models';
import { EnvironmentConfig } from '@core/config-models';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import {
  getIsExperienceEditorActive,
  JssStateDynamicPlaceholderLoaded,
} from '@core/jss-routing';
import { InnogyComponentRendering } from '@core/jss-models';
import type { DeviceInfo } from '@innogy/shared/responsive-tools';
import { MediaObserverService } from '@innogy/shared/responsive-tools';
import { Store } from '@ngrx/store';

import { getDynamicRowSettingsFromRendering } from './dynamic-row.helpers';

@Component({
  selector: 'wl-dynamic-row',
  templateUrl: './dynamic-row.component.html',
  styleUrls: [
    './dynamic-row.component.scss',
    './dynamic-row.component.ed.scss',
  ],
})
export class DynamicRowComponent implements OnInit {
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() rendering?: InnogyComponentRendering;

  readonly isXpEditorActive$ = this.store$.select(getIsExperienceEditorActive);

  @HostBinding('attr.id') get id() {
    return this.settings?.anchorPoint;
  }

  backgroundSettings?: BackgroundSettings;
  content?: BasicContentValues;
  settings?: DynamicRowSettings;
  brand?: EnvConfigBrand;
  primaryButtonTheme: ButtonTheme = 'secondary';

  unusedColumns?: string[];

  constructor(
    private readonly mediaObserver: MediaObserverService,
    private readonly store$: Store<any>,
    @Inject(ENVIRONMENT_CONFIG) private readonly _config: EnvironmentConfig
  ) {
    this.brand = this._config.brand;
  }

  deviceInfo$ = this.mediaObserver.deviceInfo$;

  private setPrimaryButtonTheme() {
    const buttonStyleString = this.content?.buttons?.primary?.style;
    const style = buttonStyleString?.includes('primary')
      ? 'primary'
      : 'secondary';
    this.primaryButtonTheme = style;
  }

  get contentTheme(): ContentTheme | undefined {
    // This breaks super easily, should match something like 'settings.theme'.
    // The idea is to set an override so cards with a transparent background
    // on a dark row automatically get a brand-alt theme for the content.
    if (this.backgroundSettings?.backgroundClass === 'bg-brand-alt') {
      return 'brand-alt';
    }
    return 'translucent';
  }

  showPlaceholderAt(index: number, isXpEditorActive: boolean) {
    // When the row is loaded in the XP editor, we always want to show the sc-placeholder so that it can be filled in
    if (isXpEditorActive) {
      return true;
    }

    const placeholderName = 'column-' + (index + 1);
    const placeholders = this.rendering?.placeholders?.[placeholderName];

    // Scenario 1: When there are any placeholders defined with fields, show the sc-placeholder.
    // Scenario 2: If there is a placeholder without fields, this (most likely) means we are dealing with a broken
    //             dataSource. In stat case we don't want to try to render the component without fields because there
    //             is nothing to show, but some styling may still be applied to the host element (e.g. when there is a
    //             broken basic-content-component placed in this placeholder, it will show the card styling, but nothing else.)
    // Scenario 3: If there is no placeholder... doesn't really matter if the <sc-placeholder> is shown or not, in
    //             both cases nothing will be visible on the client side. In this function we chose to hide the
    //             <sc-placeholder> because that is one less DOM node.
    return !!placeholders?.some(
      (placeholder) => !!(placeholder as InnogyComponentRendering).fields
    );
  }

  ngOnInit() {
    if (this.rendering) {
      this.backgroundSettings = getBackgroundSettingsFromRendering(
        this.rendering
      );
      this.content = getBasicContentValuesFromRendering(this.rendering);
      this.settings = getDynamicRowSettingsFromRendering(this.rendering);

      this.setPrimaryButtonTheme();
    }
  }

  onColumnPlaceholderLoaded(placeholderName: string) {
    this.store$.dispatch(new JssStateDynamicPlaceholderLoaded(placeholderName));
  }

  hideOnMobile(deviceInfo: DeviceInfo, columnConfig: ColumnConfig): boolean {
    return deviceInfo.isMobile && columnConfig.hideOnMobile;
  }
}
